import React, { FC, Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, Fab, styled } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { TransitionGroup } from 'react-transition-group';
import { RestorationInput } from 'models/order-input/restoration-input';
import ConstructionBox from './construction-box.component';
import AddConstructionDialog from './add-edit-construction/add-construction-dialog.component';
import DeleteConstructionConfirmationDialog from './delete-construction-confirmation-dialog.component';

type SimpleRestorationsProps = {
    restorations: RestorationInput[];
    onChange: (restorations: RestorationInput[]) => void;
    disableEdit?: boolean;
};

/** The simple and supplier flows' restorations: one card per restoration, added, edited and removed in place. */
const SimpleRestorations: FC<SimpleRestorationsProps> = ({ restorations, onChange, disableEdit }) => {
    const { t: translate } = useTranslation(['orders']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [indexBeingEdited, setIndexBeingEdited] = useState<number>();
    const [indexPendingDelete, setIndexPendingDelete] = useState<number>();
    // A stable key per card for the collapse animation; an edited restoration keeps its card's key.
    const keys = useRef(new WeakMap<RestorationInput, string>());
    const nextKey = useRef(0);
    const keyOf = (restoration: RestorationInput) => {
        if (!keys.current.has(restoration)) {
            keys.current.set(restoration, restoration._id ?? `new-${nextKey.current++}`);
        }
        return keys.current.get(restoration) as string;
    };

    const handleSave = (restoration: RestorationInput) => {
        if (indexBeingEdited === undefined) {
            onChange([...restorations, restoration]);
            return;
        }
        keys.current.set(restoration, keyOf(restorations[indexBeingEdited]));
        onChange(restorations.map((current, index) => (index === indexBeingEdited ? restoration : current)));
    };

    const handleConfirmedDelete = () => {
        if (indexPendingDelete !== undefined) {
            onChange(restorations.filter((_restoration, index) => index !== indexPendingDelete));
        }
        setIndexPendingDelete(undefined);
    };

    return (
        <Fragment>
            <Box>
                <TransitionGroup component={null}>
                    {restorations.map((restoration, index) => (
                        <Collapse key={keyOf(restoration)}>
                            <Box mb={2} sx={{ display: 'flex' }}>
                                <ConstructionBox
                                    restoration={restoration}
                                    onDeleteClick={() => setIndexPendingDelete(index)}
                                    onModifyClick={() => {
                                        setIndexBeingEdited(index);
                                        setDialogOpen(true);
                                    }}
                                    disableDelete={disableEdit}
                                />
                            </Box>
                        </Collapse>
                    ))}
                </TransitionGroup>
                {!disableEdit ? (
                    <AddConstructionButton
                        disableRipple={true}
                        onClick={() => {
                            setIndexBeingEdited(undefined);
                            setDialogOpen(true);
                        }}
                        variant="extended"
                    >
                        <AddIcon />
                        <Box>{translate('orders:ADD_CONSTRUCTION')}</Box>
                    </AddConstructionButton>
                ) : undefined}
            </Box>
            <AddConstructionDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                restoration={indexBeingEdited !== undefined ? restorations[indexBeingEdited] : undefined}
                onSave={handleSave}
            />
            <DeleteConstructionConfirmationDialog
                open={indexPendingDelete !== undefined}
                hasAddedMaterials={
                    indexPendingDelete !== undefined && !!restorations[indexPendingDelete]?.materials.length
                }
                onCancel={() => setIndexPendingDelete(undefined)}
                onConfirm={handleConfirmedDelete}
            />
        </Fragment>
    );
};

const AddConstructionButton = styled(Fab)(({ theme }) => ({
    background: 'rgba(235, 235, 235, 0.4)',
    color: theme.palette.primary.main,
    borderRadius: 5,
    width: '100%',
    minHeight: '150px',
    height: '100%',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
        background: 'rgba(235, 235, 235, 0.2)',
    },
    '& svg': {
        fontSize: 60,
        fill: theme.palette.primary.main,
        margin: 0,
        display: 'block',
    },
}));

export default SimpleRestorations;
